import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ServicesPreview from '../components/ServicesPreview';
import './ServiceRequests.css';

const ACTIVE_STATUSES = ['pending', 'accepted', 'submitted_for_review'];

const toMillis = (ts) => {
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (err) {
    return 'N/A';
  }
};

const TYPE_LABELS = {
  initial_setup: 'Initial Setup',
  update: 'Update'
};

const STATUS_META = {
  pending: { label: 'Pending', className: 'status-pending' },
  accepted: { label: 'Accepted', className: 'status-accepted' },
  submitted_for_review: { label: 'Awaiting Salon Review', className: 'status-submitted' },
  published: { label: 'Published', className: 'status-published' }
};

function ServiceRequests() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'history'
  const [activeRequests, setActiveRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [previewCache, setPreviewCache] = useState({}); // requestId -> { serviceCategories, combos } | 'loading' | 'error'

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const snapshot = await getDocs(collection(db, 'serviceRequests'));
      const all = [];
      snapshot.forEach((d) => all.push({ id: d.id, ...d.data() }));

      const active = all
        .filter((r) => ACTIVE_STATUSES.includes(r.status))
        .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

      const history = all
        .filter((r) => r.status === 'published')
        .sort((a, b) => toMillis(b.publishedAt) - toMillis(a.publishedAt));

      setActiveRequests(active);
      setHistoryRequests(history);
    } catch (err) {
      setError('Failed to load service requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleAccept = async (request) => {
    const confirmed = window.confirm(
      `Accept the service request from "${request.salonName}" and start preparing the catalog?`
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      setError('');

      await updateDoc(doc(db, 'serviceRequests', request.id), {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedBy: auth.currentUser?.email || ''
      });
      await updateDoc(doc(db, 'salons', request.salonId), {
        serviceCatalogStatus: 'accepted',
        activeServiceRequestId: request.id
      });

      navigate(`/service-requests/${request.id}/edit`);
    } catch (err) {
      alert(`Failed to accept request: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinuePreparing = (request) => {
    navigate(`/service-requests/${request.id}/edit`);
  };

  const handleRefresh = () => {
    fetchRequests();
  };

  const toggleExpand = async (request) => {
    if (expandedId === request.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(request.id);
    if (previewCache[request.id] && previewCache[request.id] !== 'error') return;

    setPreviewCache((prev) => ({ ...prev, [request.id]: 'loading' }));
    try {
      const salonSnap = await getDoc(doc(db, 'salons', request.salonId));
      const pending = salonSnap.data()?.profile?.pendingServicesData || {};
      setPreviewCache((prev) => ({
        ...prev,
        [request.id]: {
          serviceCategories: pending.serviceCategories || [],
          combos: pending.combos || []
        }
      }));
    } catch (err) {
      setPreviewCache((prev) => ({ ...prev, [request.id]: 'error' }));
    }
  };

  const renderStatusBadge = (status) => {
    const meta = STATUS_META[status] || { label: status, className: 'status-pending' };
    return <span className={`status-badge ${meta.className}`}>{meta.label}</span>;
  };

  const renderSourceInfo = (request) => {
    const source = request.source || {};
    const hasMenu = Array.isArray(source.menuFileUrls) && source.menuFileUrls.length > 0;
    if (!hasMenu && !source.websiteUrl && !source.instagramUrl && !source.notes) return null;
    return (
      <div className="svcreq-source">
        {hasMenu && (
          <div className="svcreq-source-row">
            <span className="svcreq-source-label">Menu files:</span>
            <span className="svcreq-source-value">
              {source.menuFileUrls.map((url, i) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="svcreq-source-link">
                  File {i + 1}
                </a>
              ))}
            </span>
          </div>
        )}
        {source.websiteUrl && (
          <div className="svcreq-source-row">
            <span className="svcreq-source-label">Website:</span>
            <a href={source.websiteUrl} target="_blank" rel="noopener noreferrer" className="svcreq-source-link">
              {source.websiteUrl}
            </a>
          </div>
        )}
        {source.instagramUrl && (
          <div className="svcreq-source-row">
            <span className="svcreq-source-label">Instagram:</span>
            <a href={source.instagramUrl} target="_blank" rel="noopener noreferrer" className="svcreq-source-link">
              {source.instagramUrl}
            </a>
          </div>
        )}
        {source.notes && (
          <div className="svcreq-source-row">
            <span className="svcreq-source-label">Notes:</span>
            <span className="svcreq-source-value">{source.notes}</span>
          </div>
        )}
      </div>
    );
  };

  if (error && !activeRequests.length && !historyRequests.length) {
    return (
      <div className="svcreq-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="svcreq-page">
      <div className="page-header">
        <div className="header-info">
          <h2>Service Update Requests</h2>
          <p>Review salon requests, prepare service catalog drafts, and track review status</p>
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active ({activeRequests.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History ({historyRequests.length})
        </button>
      </div>

      {loading && activeRequests.length === 0 && historyRequests.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading service requests...</p>
        </div>
      ) : (
        <>
          {activeTab === 'active' && (
            <div className="content-section">
              {activeRequests.length === 0 ? (
                <div className="empty-state">
                  <p>No active service requests</p>
                  <small>When a salon requests service setup or an update, it will appear here</small>
                </div>
              ) : (
                <div className="requests-list">
                  {activeRequests.map((request) => (
                    <div key={request.id} className="request-card">
                      <div className="request-header">
                        <div className="request-city-info">
                          <h4>{request.salonName}</h4>
                          <span className="svcreq-type-badge">{TYPE_LABELS[request.type] || request.type}</span>
                          {renderStatusBadge(request.status)}
                        </div>
                        <span className="request-date">{formatDate(request.createdAt)}</span>
                      </div>

                      {renderSourceInfo(request)}

                      <div className="request-actions">
                        {request.status === 'pending' && (
                          <button
                            className="approve-btn"
                            onClick={() => handleAccept(request)}
                            disabled={actionLoading}
                          >
                            ✓ Accept &amp; Start Preparing
                          </button>
                        )}
                        {request.status === 'accepted' && (
                          <button
                            className="approve-btn"
                            onClick={() => handleContinuePreparing(request)}
                            disabled={actionLoading}
                          >
                            Continue Preparing
                          </button>
                        )}
                        {request.status === 'submitted_for_review' && (
                          <div className="svcreq-waiting">
                            <span className="svcreq-waiting-text">⏳ Waiting for salon to review</span>
                            <button className="svcreq-expand-btn" onClick={() => toggleExpand(request)}>
                              {expandedId === request.id ? 'Hide preview' : 'View submitted draft'}
                            </button>
                          </div>
                        )}
                      </div>

                      {request.status === 'submitted_for_review' && expandedId === request.id && (
                        <div className="svcreq-preview-panel">
                          {previewCache[request.id] === 'loading' && <p>Loading draft preview...</p>}
                          {previewCache[request.id] === 'error' && (
                            <p className="error-text">Failed to load the draft preview.</p>
                          )}
                          {previewCache[request.id] &&
                            previewCache[request.id] !== 'loading' &&
                            previewCache[request.id] !== 'error' && (
                              <ServicesPreview
                                serviceCategories={previewCache[request.id].serviceCategories}
                                combos={previewCache[request.id].combos}
                              />
                            )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="content-section">
              {historyRequests.length === 0 ? (
                <div className="empty-state">
                  <p>No published requests yet</p>
                  <small>Requests move here once the salon publishes the prepared catalog</small>
                </div>
              ) : (
                <div className="requests-list">
                  {historyRequests.map((request) => (
                    <div key={request.id} className="request-card">
                      <div className="request-header">
                        <div className="request-city-info">
                          <h4>{request.salonName}</h4>
                          <span className="svcreq-type-badge">{TYPE_LABELS[request.type] || request.type}</span>
                          {renderStatusBadge(request.status)}
                        </div>
                        <span className="request-date">{formatDate(request.publishedAt)}</span>
                      </div>
                      {request.summary && (
                        <div className="svcreq-summary-row">
                          <span>+{request.summary.addedCount || 0} added</span>
                          <span>·</span>
                          <span>{request.summary.updatedCount || 0} updated</span>
                          <span>·</span>
                          <span>{request.summary.categoriesCount || 0} categories</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ServiceRequests;
